import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import React from "react";
var __jsx = React.createElement;
import { useState, useEffect } from 'react';
import { useLang } from "@/common/Contexts/LanguageContext";
import { generateYears, getDaysInMonth, getValidDays, getValidMonths, gregorianToHijri, hijriToGregorian, isHijriYear, isYearValid } from './DateCalendarHelpers';
import { DateCalendarJSX } from './DateCalendarJSX';
import { gregorianMonths, hijriMonths, labels } from "@componentsShared/DateCalendarGregorian/data";
var DateCalendarGregorian = function DateCalendarGregorian(_ref) {
  var label = _ref.label,
    yearsFrom = _ref.yearsFrom,
    yearsTo = _ref.yearsTo,
    errorMessage = _ref.errorMessage,
    value = _ref.value,
    onChange = _ref.onChange,
    minValidDate = _ref.minValidDate,
    maxValidDate = _ref.maxValidDate;
  var lang = useLang();

  // Single state for calendar data
  var _useState = useState({
      day: (value === null || value === void 0 ? void 0 : value.day) || null,
      month: (value === null || value === void 0 ? void 0 : value.month) || null,
      year: (value === null || value === void 0 ? void 0 : value.year) || null,
      type: (value === null || value === void 0 ? void 0 : value.type) || 'G'
    }),
    calendarState = _useState[0],
    setCalendarState = _useState[1];

  // Error state
  var _useState2 = useState(errorMessage),
    error = _useState2[0],
    setError = _useState2[1];
  useEffect(function () {
    setError(errorMessage);
  }, [errorMessage]);

  // Handle calendar type change
  var handleCalendarTypeChange = function handleCalendarTypeChange(newType) {
    if (newType === calendarState.type) return;
    // Store current date values before switching
    var currentDay = calendarState.day;
    var currentMonth = calendarState.month;
    var currentYear = calendarState.year;
    setError(null);

    // Do not convert if we don't have a complete date
    if (currentDay === null || currentMonth === null || currentYear === null) {
      setCalendarState(function (prev) {
        return _objectSpread(_objectSpread({}, prev), {}, {
          type: newType
        });
      });
      // Notify parent that date is incomplete/invalid
      onChange(null);
      return;
    }

    // Convert between calendar systems when switching
    if (newType === 'H') {
      if (isHijriYear(currentYear)) {
        return setCalendarState(_objectSpread(_objectSpread({}, calendarState), {}, {
          type: newType
        }));
      }
      // Convert from Gregorian to Hijri
      try {
        var hijri = gregorianToHijri(new Date(currentYear, currentMonth, currentDay, 13));
        if (hijri) {
          setCalendarState({
            day: hijri.day,
            month: hijri.month,
            year: hijri.year,
            type: newType
          });
        } else {
          // Invalid conversion
          setCalendarState({
            day: null,
            month: null,
            year: null,
            type: newType
          });
          setError(labels.error[lang]);
        }
      } catch (error) {
        console.error('Error converting to Hijri:', error);
        setCalendarState({
          day: null,
          month: null,
          year: null,
          type: newType
        });
        setError(labels.error[lang]);
      }
    } else if (newType === 'G') {
      if (!isHijriYear(currentYear)) {
        return setCalendarState(_objectSpread(_objectSpread({}, calendarState), {}, {
          type: newType
        }));
      }
      // Convert from Hijri to Gregorian
      try {
        var gregorian = hijriToGregorian(currentDay, currentMonth, currentYear);
        if (gregorian && !isNaN(gregorian.getTime())) {
          setCalendarState({
            day: gregorian.getDate(),
            month: gregorian.getMonth(),
            year: gregorian.getFullYear(),
            type: newType
          });
        } else {
          // Invalid conversion
          setCalendarState({
            day: null,
            month: null,
            year: null,
            type: newType
          });
          setError(labels.error[lang]);
        }
      } catch (error) {
        console.error('Error converting to Gregorian:', error);
        setCalendarState({
          day: null,
          month: null,
          year: null,
          type: newType
        });
        setError(labels.error[lang]);
      }
    }
  };

  // Handle year change
  var handleYearChange = function handleYearChange(e) {
    var newYear = parseInt(e.target.value, 10);

    // Reset month and day since year has changed
    setCalendarState(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, {
        year: newYear,
        month: null,
        day: null
      });
    });
    setError(null);
  };

  // Handle month change
  var handleMonthChange = function handleMonthChange(e) {
    var newMonth = parseInt(e.target.value, 10);

    // Reset day since month has changed
    setCalendarState(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, {
        month: newMonth,
        day: null
      });
    });
    setError(null);
  };

  // Handle day change
  var handleDayChange = function handleDayChange(e) {
    var newDay = parseInt(e.target.value, 10);
    setCalendarState(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, {
        day: newDay
      });
    });
    setError(null);
  };

  // Validate and update date - This is now called only by the useEffect
  var validateAndUpdateDate = function validateAndUpdateDate() {
    var day = calendarState.day,
      month = calendarState.month,
      year = calendarState.year,
      type = calendarState.type;
    // Check if we have all required data for a valid date
    if (day === null || month === null || year === null) {
      // If any part is null, trigger onChange with null
      onChange(null);
      return;
    }
    var maxDays = getDaysInMonth(month, year, type === 'H');
    if (day > maxDays) {
      setError(labels.error[lang]);
      // Trigger onChange with null for invalid date
      onChange(null);
      return;
    }
    setError(null);
    try {
      var dateToValidate;
      if (isHijriYear(year)) {
        dateToValidate = hijriToGregorian(day, month, year);
      } else {
        dateToValidate = new Date(year, month, day, 13);
      }
      console.log('dateToValidate', {
        type: type
      }, dateToValidate);
      if (dateToValidate && !isNaN(dateToValidate.getTime())) {
        onChange({
          day: dateToValidate.getDate(),
          year: dateToValidate.getFullYear(),
          month: dateToValidate.getMonth() + 1,
          type: type
        });
      } else {
        // If conversion resulted in invalid date, pass null
        onChange(null);
      }
    } catch (error) {
      console.error('Error in validateAndUpdateDate:', error);
      // If there was an error, pass null
      onChange(null);
    }
  };

  // MAIN EFFECT: Validate and update date when date parts change
  // This is the centralized place where onChange gets triggered
  useEffect(function () {
    // Call validateAndUpdateDate to trigger onChange with the appropriate value
    validateAndUpdateDate();
  }, [calendarState.day, calendarState.month, calendarState.year]);

  // Generate arrays for dropdowns
  var years = generateYears(calendarState.type, yearsFrom, yearsTo, isYearValid({
    calendarType: calendarState.type,
    minValidDate: minValidDate,
    maxValidDate: maxValidDate
  }));
  var validMonths = calendarState.year !== null ? getValidMonths({
    calendarType: calendarState.type,
    minValidDate: minValidDate,
    maxValidDate: maxValidDate
  }, calendarState.year) : [];
  var validDays = calendarState.year !== null && calendarState.month !== null ? getValidDays({
    calendarType: calendarState.type,
    minValidDate: minValidDate,
    maxValidDate: maxValidDate
  }, calendarState.year, calendarState.month) : [];

  // Get months for display
  var monthsForDisplay = (calendarState.type === 'G' ? gregorianMonths : hijriMonths)[lang];
  return /*#__PURE__*/React.createElement(DateCalendarJSX, {
    day: calendarState.day,
    month: calendarState.month,
    year: calendarState.year,
    error: error,
    calendarType: calendarState.type,
    years: years,
    validMonths: validMonths,
    validDays: validDays,
    months: monthsForDisplay,
    handleYearChange: handleYearChange,
    handleMonthChange: handleMonthChange,
    handleDayChange: handleDayChange,
    handleCalendarTypeChange: handleCalendarTypeChange,
    label: label
  });
};
export default DateCalendarGregorian;